// ============================================================
// api/adminClients.js
// Endpoint bindings for the mgmt/admin/clients endpoints
// (Workstream 1, shipped May 2026).
//
// Separate from api/clients.js (user-tenant mgmt/clients) by
// design: distinct routes, distinct response shapes. The
// user-tenant module is consumed by the existing
// InvitationsPage's client dropdown.
//
// Backend reference:
//   backend/func-carehub-api/src/functions/admin-clients-admin-list.js
//   backend/func-carehub-api/src/functions/admin-clients-create.js
//   backend/func-carehub-api/src/functions/admin-clients-detail.js
// ============================================================

import { apiDelete, apiGet, apiPatch, apiPost } from './client';

/**
 * List Clients across all Tenants with pagination + optional filters.
 *
 * @param {Object} [params={}]
 * @param {number} [params.page=1]      1-indexed
 * @param {number} [params.pageSize=50] 1..200
 * @param {number} [params.tenantId]    Filter to one Tenant
 * @param {string} [params.search]      Substring against ClientName / DisplayName
 *
 * @returns {Promise<{
 *   items: Array<{
 *     clientId: number,
 *     clientName: string,
 *     displayName: string|null,
 *     clientType: 'care_home'|'domiciliary_agency'|'mixed',
 *     tenantId: number|null,
 *     tenantName: string|null,
 *     isActive: boolean,
 *     lifecycleStatus: 'trial'|'active'|'disabled',
 *     createdAt: string|null,
 *     userCount: number,
 *   }>,
 *   totalCount: number, page: number, pageSize: number,
 * }>}
 */
export async function listAdminClients(params = {}) {
  return apiGet('/mgmt/admin/clients', params);
}

/**
 * Create a Client. LifecycleStatus defaults to 'trial_active' server-side.
 *
 * @param {Object} body
 * @param {string} body.clientName
 * @param {('care_home'|'domiciliary_agency'|'mixed')} body.clientType
 * @param {number} [body.tenantId]   Null/omitted for Pathway 3
 *
 * @returns {Promise<{
 *   clientId: number, clientName: string, displayName: string|null,
 *   clientType: string, tenantId: number|null, tenantName: string|null,
 *   lifecycleStatus: 'trial', isActive: boolean, createdAt: string|null,
 * }>}
 */
export async function createAdminClient(body) {
  return apiPost('/mgmt/admin/clients', body);
}

/**
 * Single-Client detail header.
 *
 * Backend reference:
 *   backend/func-carehub-api/src/functions/admin-clients-detail.js
 *
 * @param {number} clientId
 *
 * @returns {Promise<{
 *   clientId: number, clientName: string, displayName: string|null,
 *   clientType: 'care_home'|'domiciliary_agency'|'mixed',
 *   tenantId: number|null, tenantName: string|null,
 *   lifecycleStatus: 'trial'|'active'|'disabled', isActive: boolean,
 *   createdAt: string|null, updatedAt: string|null,
 *   deactivatedAt: string|null, deactivatedBy: string|null,
 *   manualInviteRequired: boolean, manualInviteReason: string|null,
 * }>}
 *
 * Throws ApiError with status 404 + message 'client_not_found' when
 * the client doesn't exist.
 */
export async function getAdminClientDetail(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}`);
}

/**
 * Rename a Client (PATCH /mgmt/admin/clients/{id}, withAudit). Added
 * 2026-09-25; before it a client name was fixed at creation.
 *
 * @param {number} clientId
 * @param {string} clientName  1-200 chars
 * @returns {Promise<{ clientId:number, clientName:string, wasIdempotent:boolean }>}
 */
export async function renameAdminClient(clientId, clientName) {
  if (!Number.isInteger(clientId) || clientId <= 0) {
    throw new TypeError(`renameAdminClient: clientId must be a positive integer, got: ${JSON.stringify(clientId)}`);
  }
  return apiPatch(`/mgmt/admin/clients/${clientId}`, { clientName });
}

/**
 * Mark a Client as needing a manual invitation (trial-approval
 * auto-invite failed at the issueInvitation step). withAudit.
 *
 * @param {number} clientId
 * @param {string} reason  1-500 chars (HTTP status + sanitised body,
 *                         or a network-error message)
 * @returns {Promise<{ clientId:number, manualInviteRequired:boolean,
 *   manualInviteReason:string|null, wasIdempotent:boolean }>}
 */
export async function markClientManualInviteRequired(clientId, reason) {
  return apiPost(`/mgmt/admin/clients/${clientId}/mark-manual-invite-required`, { reason });
}

/**
 * Clear a Client's manual-invite flag (a re-send succeeded). Same
 * endpoint, clear action. withAudit.
 *
 * @param {number} clientId
 * @returns {Promise<{ clientId:number, manualInviteRequired:boolean,
 *   manualInviteReason:string|null, wasIdempotent:boolean }>}
 */
export async function clearClientManualInviteRequired(clientId) {
  return apiPost(`/mgmt/admin/clients/${clientId}/mark-manual-invite-required`, { clear: true });
}

/**
 * Users belonging to a Client.
 *
 * @param {number} clientId
 * @param {Object} [params={}]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {('manager'|'carer'|'admin'|'auditor')} [params.roleKey]
 *
 * @returns {Promise<{
 *   items: Array<{
 *     clientUserId: number,
 *     email: string,
 *     externalIdUserId: string|null,
 *     displayName: string|null,
 *     roleKey: 'manager'|'carer'|'admin'|'auditor',
 *     requiresAgentSetup: boolean,
 *     isActive: boolean,
 *     accessExpiresAt: string|null,
 *     createdAt: string|null,
 *   }>,
 *   totalCount: number, page: number, pageSize: number,
 * }>}
 */
export async function listAdminClientUsers(clientId, params = {}) {
  return apiGet(`/mgmt/admin/clients/${clientId}/users`, params);
}

/**
 * Modules with per-Client enable state. No pagination.
 *
 * @param {number} clientId
 *
 * @returns {Promise<{ items: Array<{
 *   moduleId: number, moduleKey: string, moduleName: string,
 *   isActiveGlobally: boolean, isActiveForClient: boolean,
 * }> }>}
 */
export async function listAdminClientModules(clientId) {
  return apiGet(`/mgmt/admin/clients/${clientId}/modules`);
}

/**
 * Set one module's lifecycle state for one Client (Platform-06).
 *
 * @param {number} clientId
 * @param {string} moduleKey
 * @param {('active'|'readonly'|'deactivated_for_client')} lifecycleStatus
 * @param {string} [reason]  optional operator note (stored on deactivation)
 *
 * @returns {Promise<{
 *   clientId: number, moduleKey: string, moduleId: number,
 *   lifecycleStatus: string, isActive: boolean, updatedAt: string|null,
 *   wasIdempotent: boolean,
 * }>}
 */
export async function toggleAdminClientModule(clientId, moduleKey, lifecycleStatus, reason) {
  const body = reason != null ? { lifecycleStatus, reason } : { lifecycleStatus };
  return apiPatch(`/mgmt/admin/clients/${clientId}/modules/${moduleKey}`, body);
}

/**
 * Per-Client UsageLog aggregation.
 *
 * Backend supports groupBy='day' only — each row is one
 * (date, moduleKey) pair across the requested window.
 *
 * @param {number} clientId
 * @param {Object} [params={}]
 * @param {string} [params.startDate]  ISO-8601 date; default 30 days ago
 * @param {string} [params.endDate]    ISO-8601 date; default now
 * @param {string} [params.groupBy='day']
 *
 * @returns {Promise<{
 *   items: Array<{
 *     date: string, moduleKey: string,
 *     invocationCount: number, userCount: number, tokensUsed: number,
 *   }>,
 *   totalInvocations: number, totalTokens: number,
 * }>}
 */
export async function getAdminClientUsage(clientId, params = {}) {
  return apiGet(`/mgmt/admin/clients/${clientId}/usage`, params);
}

/**
 * Per-Client AuditLog history.
 *
 * @param {number} clientId
 * @param {Object} [params={}]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {string} [params.action]
 * @param {string} [params.entityType]
 *
 * @returns {Promise<{
 *   items: Array<{
 *     auditLogId: number|string, entityType: string, entityId: number|null,
 *     action: string, changedBy: string|null, changedAt: string|null,
 *     oldValues: object|null, newValues: object|null, details: string|null,
 *   }>,
 *   totalCount: number, page: number, pageSize: number,
 * }>}
 */
export async function getAdminClientHistory(clientId, params = {}) {
  return apiGet(`/mgmt/admin/clients/${clientId}/history`, params);
}

/**
 * Global Users list across all Clients and Tenants.
 *
 * Backend reference:
 *   backend/func-carehub-api/src/functions/admin-users-list.js
 *
 * NOTE on `search`: the backend handler exposes a `search` param
 * (substring match against Email / DisplayName), but the admin
 * UI deliberately does not surface a search input in v1 — flagged
 * for review during Session 2b.
 *
 * @param {Object} [params={}]
 * @param {number} [params.page=1]
 * @param {number} [params.pageSize=50]
 * @param {number} [params.clientId]
 * @param {('manager'|'carer'|'admin'|'auditor')} [params.roleKey]
 * @param {boolean} [params.includeInactive=false]
 * @param {boolean} [params.requiresSetupOnly=false]
 *   Filters to admin-role users with zero active ClientUserAgents grants —
 *   the same predicate that drives the "Setup required" badge.
 *
 * @returns {Promise<{
 *   items: Array<{
 *     clientUserId: number,
 *     email: string,
 *     externalIdUserId: string|null,
 *     displayName: string|null,
 *     clientId: number,
 *     clientName: string,
 *     tenantId: number|null,
 *     tenantName: string|null,
 *     roleKey: 'manager'|'carer'|'admin'|'auditor',
 *     requiresAgentSetup: boolean,
 *     isActive: boolean,
 *     accessExpiresAt: string|null,
 *     createdAt: string|null,
 *   }>,
 *   totalCount: number, page: number, pageSize: number,
 * }>}
 */
export async function listAdminUsers(params = {}) {
  return apiGet('/mgmt/admin/users', params);
}

/**
 * Single user detail.
 *
 * Backend reference:
 *   backend/func-carehub-api/src/functions/admin-users-detail.js
 *
 * @param {number} clientUserId
 *
 * @returns {Promise<{
 *   clientUserId: number,
 *   email: string,
 *   externalIdUserId: string|null,
 *   displayName: string|null,
 *   jobTitle: string|null,
 *   clientId: number,
 *   clientName: string,
 *   tenantId: number|null,
 *   tenantName: string|null,
 *   roleKey: 'manager'|'carer'|'admin'|'auditor',
 *   requiresAgentSetup: boolean,
 *   isActive: boolean,
 *   accessExpiresAt: string|null,
 *   createdAt: string|null,
 *   deactivatedAt: string|null,
 *   deactivatedBy: string|null,
 *   lastLoginAt: string|null,
 * }>}
 */
export async function getAdminUserDetail(clientUserId) {
  return apiGet(`/mgmt/admin/users/${clientUserId}`);
}

/**
 * Extend an auditor's AccessExpiresAt. Auditor-only.
 *
 * @param {number} clientUserId
 * @param {string} newAccessExpiresAt  ISO-8601 UTC; must be future and ≤90 days from now
 */
export async function extendAdminUserAccess(clientUserId, newAccessExpiresAt) {
  return apiPost(`/mgmt/admin/users/${clientUserId}/extend-access`, { newAccessExpiresAt });
}

/** Deactivate a user (sets IsActive=0). */
export async function deactivateAdminUser(clientUserId) {
  return apiPost(`/mgmt/admin/users/${clientUserId}/deactivate`, {});
}

/** Reactivate a previously deactivated user. */
export async function reactivateAdminUser(clientUserId) {
  return apiPost(`/mgmt/admin/users/${clientUserId}/reactivate`, {});
}

/**
 * Change a user's CORE role (2026-09-09).
 * @param {number} clientUserId
 * @param {'manager'|'carer'|'admin'|'auditor'} roleKey
 * @returns {Promise<{clientUserId:number, roleKey:string, previousRoleKey:string, moduleRolesRevoked:Array<{module:string,role:string}>, accessExpiryCleared:boolean}>}
 */
export async function changeAdminUserRole(clientUserId, roleKey) {
  return apiPost(`/mgmt/admin/users/${clientUserId}/role`, { roleKey });
}

/**
 * ClientUserAgents rows for one user (active + revoked).
 *
 * @returns {Promise<{ items: Array<{
 *   moduleId: number, moduleKey: string, moduleName: string,
 *   grantedAt: string|null, grantedBy: string|null,
 *   revokedAt: string|null, revokedBy: string|null,
 * }> }>}
 */
export async function listAdminUserEntitlements(clientUserId) {
  return apiGet(`/mgmt/admin/users/${clientUserId}/entitlements`);
}

/**
 * Grant a module to an admin-role user. Body { moduleKey }.
 */
export async function grantAdminUserEntitlement(clientUserId, moduleKey) {
  return apiPost(`/mgmt/admin/users/${clientUserId}/entitlements`, { moduleKey });
}

/**
 * Revoke an active grant for one module on one admin-role user.
 */
export async function revokeAdminUserEntitlement(clientUserId, moduleKey) {
  return apiDelete(`/mgmt/admin/users/${clientUserId}/entitlements/${encodeURIComponent(moduleKey)}`);
}

// ── Client-level lifecycle (Platform-06) ────────────────────
// These three endpoints shipped with the state machine and had NO portal
// binding until 2026-08-04, so a client could be SEEN in a lifecycle state
// (the badge) but never moved through one.

/**
 * Drive an operator transition on a client's own lifecycle.
 *
 * @param {number} clientId
 * @param {('expire'|'terminate'|'deactivate'|'reactivate')} transition
 *   expire:     trial_active       -> trial_expired      (+3mo retention)
 *   terminate:  client_active      -> client_terminating (30-day export window)
 *   deactivate: client_terminating -> client_deactivated (+6yr retention)
 *   reactivate: client_terminating -> client_active
 * @param {string} [reason]
 * @returns {Promise<{ clientId: number, lifecycleStatus: string, isActive: boolean }>}
 *   409 illegal_transition carries currentStatus.
 */
export async function transitionAdminClientLifecycle(clientId, transition, reason) {
  return apiPost(`/mgmt/admin/clients/${clientId}/lifecycle-transition`,
    reason ? { transition, reason } : { transition });
}

/**
 * What disabling ONE module does to the client's other active modules.
 * `moduleKey` is required. Built for the pre-confirmation warning and never
 * called until 2026-08-04 — the cascade was computed and shown to nobody.
 */
export async function getAdminClientAffectedModules(clientId, { moduleKey, targetStatus } = {}) {
  const qs = new URLSearchParams();
  if (moduleKey) qs.set('moduleKey', moduleKey);
  if (targetStatus) qs.set('targetStatus', targetStatus);
  const q = qs.toString();
  return apiGet(`/mgmt/admin/clients/${clientId}/lifecycle-affected-modules${q ? `?${q}` : ''}`);
}

/**
 * Set a trial client's conversion clock. Only valid while trial_active.
 * A trial with no date never auto-expires (LD-9.1) — that is the designed
 * default, not a defect.
 *
 * @param {number} clientId
 * @param {string} trialEndsAt ISO-8601 datetime
 */
export async function setAdminClientTrialEnd(clientId, trialEndsAt) {
  return apiPost(`/mgmt/admin/clients/${clientId}/set-trial-end`, { trialEndsAt });
}
